import React, { ReactNode, useCallback, useRef } from "react";
import styled from "styles/styled_components";

import Button, { ButtonColor } from "components/shared/button";
import Icon, { ICON_TYPE, ICON_COLOR } from "components/shared/icon";
import {
  StyledExpanderWrapper,
  StyledExpanderContent,
} from "components/shared/dropdown_expander";

import { noopWhenDisabled } from "helpers/general";
import useOnDocumentClick from "helpers/hooks/useOnDocumentClick";
import useOnEscapeKey from "helpers/hooks/useOnEscapeKey";
import useToggle from "helpers/hooks/useToggle";

const StyledSpilloverOption = styled("li")<ISpilloverOptionProps>`
  opacity: ${props => (props.disabled ? "0.5" : "1")};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.large}`};
  ${props =>
    !props.disabled &&
    `
    &:hover {
      background-color: ${props.theme.colors.brandGray10};
      cursor: pointer;
  `}
`;

const StyledOptionsWrapper = styled.div`
  padding: ${props => props.theme.spacing.small} 0;
  width: ${props => `calc(30 * ${props.theme.gridBase})`};
`;

export interface ISpilloverProps {
  optionGroups: ISpilloverOptionProps[][];
}

export interface ISpilloverOptionProps {
  children: ReactNode;
  disabled?: boolean;
  onClick: (event?: React.MouseEvent) => void;
}

function SpilloverMenu({ optionGroups }: ISpilloverProps) {
  const [expanded, toggleExpanded] = useToggle(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(
    () => {
      if (expanded) {
        toggleExpanded();
      }
    },
    [expanded]
  );
  useOnDocumentClick(close, menuRef);
  useOnEscapeKey(close);

  let expandedOptions: JSX.Element[] = [];
  if (expanded) {
    expandedOptions = optionGroups.reduce(
      (fullList: JSX.Element[], options: ISpilloverOptionProps[], groupIdx) => {
        const groupOfOptions = options.map(
          (optionProps: ISpilloverOptionProps, idx: number) => {
            const onClick = noopWhenDisabled(
              optionProps.onClick,
              optionProps.disabled as boolean
            );
            return (
              <StyledSpilloverOption
                key={`${groupIdx}-${idx}`}
                {...optionProps}
                onClick={onClick}
              />
            );
          }
        );
        if (groupIdx > 0) {
          // Second group and after are divided by a line
          groupOfOptions.unshift(<hr key={`divider-${groupIdx}`} />);
        }
        return fullList.concat(groupOfOptions);
      },
      expandedOptions
    );
  }

  return (
    <StyledExpanderWrapper>
      <Button
        buttonColor={ButtonColor.BLUE}
        label="More"
        onClick={toggleExpanded}
        ref={menuRef}
        iconLeft={
          <Icon
            size="12px"
            type={ICON_TYPE.MORE_VERT}
            color={ICON_COLOR.WHITE}
          />
        }
      />
      {expandedOptions.length > 0 && (
        <StyledExpanderContent>
          <StyledOptionsWrapper>{expandedOptions}</StyledOptionsWrapper>
        </StyledExpanderContent>
      )}
    </StyledExpanderWrapper>
  );
}

export default SpilloverMenu;
